/**
 * Scan-mode banner (DEC-067, UI_SDD v1.2).
 *
 * Every scan self-declares how much AI was involved. Amber informational
 * tone for degraded modes: this is a business mode, never an error.
 */
import { useProfile } from '../hooks/useAuth'
import type { LlmMode } from '../types/api'
import { AGENT_LABELS } from '../types/api'
import { SETTINGS_PATH } from './settings/settingsSections'

interface ScanModeBannerProps {
  llmMode: LlmMode | undefined
  reason?: string | null
  fromAgent?: string | null
  /** Rendered inside the awaiting_decision banner (e.g. decision buttons). */
  children?: React.ReactNode
}

const REASON_TEXT: Record<string, string> = {
  credit_exhausted: 'Anthropic credit balance exhausted',
  auth_invalid: 'Anthropic API key rejected',
}

export function ScanModeBanner({ llmMode, reason, fromAgent, children }: ScanModeBannerProps) {
  const profile = useProfile()
  if (!llmMode || llmMode === 'full') return null

  const agentLabel = fromAgent ? (AGENT_LABELS[fromAgent] ?? fromAgent) : null
  const reasonText = reason ? (REASON_TEXT[reason] ?? reason) : null

  if (llmMode === 'static_by_choice') {
    return (
      <div className="flex items-center gap-2.5 border border-hairline bg-panel rounded-lg shadow-card px-4 py-2.5 mb-4">
        <span className="w-2 h-2 rounded-full bg-faint flex-none" />
        <span className="text-[12px] text-body">
          <span className="font-semibold">Static-only scan.</span> AI validation was turned off
          for this scan. Findings come from TDIA&apos;s static detectors only.
        </span>
      </div>
    )
  }

  if (llmMode === 'not_configured') {
    const canConfigure = profile === null || profile === 'admin'
    return (
      <div className="flex items-center gap-2.5 border border-hairline bg-panel rounded-lg shadow-card px-4 py-2.5 mb-4">
        <span className="w-2 h-2 rounded-full bg-faint flex-none" />
        <span className="text-[12px] text-body">
          <span className="font-semibold">AI enrichment is off.</span> No Anthropic API key is
          configured for this workspace. Findings come from TDIA&apos;s static detectors only.
          {canConfigure && (
            <>
              {' '}
              <a href={SETTINGS_PATH.general} className="text-accent-text font-semibold hover:underline">
                Configure a key
              </a>
              .
            </>
          )}
        </span>
      </div>
    )
  }

  // awaiting_decision / degraded_credits / degraded_auth → amber informational
  return (
    <div className="flex items-start gap-2.5 border border-warn/40 bg-chip-warn rounded-lg px-4 py-2.5 mb-4">
      <span className="w-2 h-2 rounded-full bg-warn flex-none mt-[5px]" />
      <div className="text-[12px] text-body">
        {llmMode === 'awaiting_decision' ? (
          <>
            <span className="font-semibold">AI became unavailable{agentLabel ? ` at ${agentLabel}` : ''}</span>
            {reasonText ? `: ${reasonText}.` : '.'} The scan is paused and waiting for your
            decision.
            {children}
          </>
        ) : (
          <>
            <span className="font-semibold">
              This scan switched to static-only{agentLabel ? ` at ${agentLabel}` : ''}
            </span>
            {reasonText ? `: ${reasonText}.` : '.'} Findings from later agents were produced
            without AI validation or enrichment. A full-AI re-scan is worth running once access
            is restored.
          </>
        )}
      </div>
    </div>
  )
}
